import React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DetailedStartupCard } from 'TEIA/components/DetailedStartupCard'
import { useGet } from 'sharedKernel/api'
import { Loading } from 'components/loading'

const displayStr = (v: unknown): string => {
  if (v === null || v === undefined) return '-'
  const s = String(v).trim()
  return s === '' ? '-' : s
}

const displayNum = (v: unknown): string => {
  if (v === null || v === undefined) return '-'
  if (typeof v === 'number' && Number.isNaN(v)) return '-'
  return String(v)
}

export const HubScoutingStartupPage = (): JSX.Element => {
  const { startupId } = useParams()
  const location = useLocation() as { state?: { startup?: any } }
  const navigate = useNavigate()

  const startupFromState = location.state?.startup

  const request = startupFromState ? { url: '' } : { url: `/v1/hub/company/${startupId ?? ''}` }
  const { data: startupResponse, isFetching } = useGet<any>(
    request,
    'scouting-startup-details',
    { enabled: !startupFromState && !!startupId }
  )

  const startup = startupFromState ?? startupResponse?.data

  if (isFetching) {
    return <Loading />
  }

  if (startup == null) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">Startup não encontrada.</div>
      </div>
    )
  }

  const addressParts = [
    startup.addressLineOne,
    startup.addressNumber,
    startup.addressLineTwo,
    startup.addressNeighborhood,
    startup.addressCity,
    startup.addressState,
    startup.addressZipcode,
    startup.addressCountry
  ]
    .map((p: unknown) => (p != null ? String(p).trim() : ''))
    .filter((p: string) => p !== '')

  const address = addressParts.length > 0 ? addressParts.join(', ') : ''

  const developmentStageLabels: Record<string | number, string> = {
    1: 'Ainda não realizei vendas do produto',
    2: 'Já realizei a primeira venda',
    3: 'Já tenho vendas recorrentes'
  }

  const developmentStageRaw = startup.developmentStage ?? startup.startupInfo?.developmentStage
  const developmentStageValue =
    developmentStageRaw != null && developmentStageRaw !== ''
      ? (developmentStageLabels[developmentStageRaw] ?? String(developmentStageRaw))
      : ''

  const startupName = startup.name ?? startup.companyName ?? ''

  const pitchSource = startup.description ?? startup.pitch ?? startup.startupInfo?.pitch

  return (
    <div className="container py-4">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <div
          role="button"
          aria-label="Voltar"
          onClick={() => navigate(-1)}
          style={{
            width: '32px',
            height: '32px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#0066B3',
            cursor: 'pointer'
          }}
        >
          <FontAwesomeIcon icon={['fas', 'arrow-left']} style={{ width: '32px', height: '32px' }} />
        </div>
        <div
          style={{
            color: '#464646',
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '120%'
          }}
        >
          {displayStr(startupName)}
        </div>
      </div>

      <DetailedStartupCard
        customPrimaryColor='#0066B3'
        defaultCollapsed={false}
        logo={typeof startup.logo === 'string' && startup.logo.trim() !== '' ? startup.logo : (typeof startup.profileImage === 'string' && startup.profileImage.trim() !== '' ? startup.profileImage : undefined)}
        pitch={displayStr(pitchSource)}
        contactName={displayStr(startup.contactName)}
        contactPhone={displayStr(startup.contactPhone ?? startup.contactMobile)}
        contactEmail={displayStr(startup.contactEmail ?? startup.emailContact)}
        CNPJ={displayStr(startup.cnpj)}
        numberEmployees={displayStr(startup.numberEmployees ?? startup.startupInfo?.numberEmployees)}
        website={displayStr(startup.website)}
        activity={displayStr(startup.category ?? startup.activity ?? startup.startupInfo?.activity)}
        businessModel={displayStr(startup.businessModel ?? startup.startupInfo?.businessModel)}
        target={displayStr(startup.target ?? startup.startupInfo?.target)}
        technologies={displayStr(startup.technologies ?? startup.startupInfo?.technologies)}
        developmentStage={displayStr(developmentStageValue)}
        annualTurnover={displayNum(startup.annualTurnover ?? startup.startupInfo?.annualTurnover)}
        investmentCaptured={displayNum(
          startup.investmentCaptured ?? startup.amount ?? startup.startupInfo?.amount
        )}
        address={displayStr(address)}
      />
    </div>
  )
}
